import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { FuseDrawerComponent } from '@fuse/components/drawer';
import { OnlineHelpService } from './online-help.service';
import { Subject, takeUntil } from 'rxjs';

/**
 * Displays a side-drawer on the right edge of the screen with rendered markdown help content.
 *
 * This component is placed once in the global layout shell and controlled entirely through
 * the `OnlineHelpService`. When a help document is loaded via `showDocument()`, the drawer
 * opens automatically. Internal links within the rendered HTML are intercepted so they
 * navigate within the help system instead of triggering browser navigation. External links
 * open in a new tab.
 */
@Component({
  selector: 'online-help',
  standalone: true,
  imports: [
    AsyncPipe,
    MatButtonModule,
    MatDividerModule,
    MatIconModule,
    FuseDrawerComponent,
  ],
  templateUrl: './online-help.component.html',
  styleUrls: ['./online-help.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class OnlineHelpComponent implements OnInit, OnDestroy {
  /** Reference to the Fuse drawer that hosts the help content */
  @ViewChild('drawer') drawer!: FuseDrawerComponent;

  /** Reference to the scrollable content container (used to reset scroll on navigation) */
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;

  /** The currently active help document as an observable stream */
  activeDocument$ = this._onlineHelpService.activeDocument$;

  private _unsubscribeAll: Subject<any> = new Subject<any>();

  constructor(private _onlineHelpService: OnlineHelpService) {}

  ngOnInit(): void {
    // Open or close the drawer whenever the active document changes
    this._onlineHelpService.activeDocument$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((activeDocument) => {
        if (activeDocument) {
          this.drawer?.open();
          this._scrollToTop();
        } else {
          this.drawer?.close();
        }
      });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  /** Closes the help drawer */
  close(): void {
    this._onlineHelpService.hideDocument();
  }

  /**
   * Intercepts clicks on rendered HTML content. Internal links (relative paths) navigate
   * within the help system, while external links (http/https) open in a new browser tab.
   */
  innerHtmlClicked(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const target = event.target as HTMLElement;

    // Only handle anchor tag clicks
    if (target.tagName !== 'A') {
      return;
    }

    const anchor = target as HTMLAnchorElement;
    const hrefAttr = anchor.getAttribute('href');

    if (!hrefAttr) {
      return;
    }

    // External links open in a new tab
    if (hrefAttr.startsWith('http')) {
      window.open(anchor.href, '_blank');
      return;
    }

    // Internal links navigate within the help system
    const url = new URL(anchor.href);
    this._onlineHelpService.showDocument(url.pathname);
  }

  canGoBack(): boolean {
    return this._onlineHelpService.canGoBack();
  }

  canGoForward(): boolean {
    return this._onlineHelpService.canGoForward();
  }

  goBack(): void {
    this._onlineHelpService.goBack();
  }

  goForward(): void {
    this._onlineHelpService.goForward();
  }

  /** Resets the scroll position to the top when navigating to a new document */
  private _scrollToTop(): void {
    if (!this.scrollContainer) {
      return;
    }
    this.scrollContainer.nativeElement.scrollTop = 0;
  }
}
